import React, { useState, useEffect, useCallback } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ShiftTemplateEntryDto } from '../../api/models/ShiftTemplateEntryDto';
import { useErrorHandler } from '../../utils/errorHandler';
import { resolveValidationMessage } from '../../utils/validationMessages';

// Validation schema for shift template entry
const shiftEntrySchema = z.object({
  shiftName: z.string().min(1, 'validation.required'),
  startTime: z.string().min(1, 'validation.required'),
  endTime: z.string().min(1, 'validation.required'),
  breakStart: z.string().optional(),
  breakEnd: z.string().optional(),
  monday: z.boolean(),
  tuesday: z.boolean(),
  wednesday: z.boolean(),
  thursday: z.boolean(),
  friday: z.boolean(),
  saturday: z.boolean(),
  sunday: z.boolean()
});

type ShiftEntryFormData = z.infer<typeof shiftEntrySchema>;

export type ShiftEntryFormProps = {
  value: ShiftTemplateEntryDto | null;
  onClose: () => void;
  onSave: (value: ShiftTemplateEntryDto) => Promise<void> | void;
};

const ShiftEntryFormDialog: React.FC<ShiftEntryFormProps> = ({ value, onClose, onSave }) => {
  const { t } = useTranslation(['common']);
  const [visible, setVisible] = useState(false);
  const { handleError } = useErrorHandler();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    // watch
  } = useForm<ShiftEntryFormData>({
    resolver: zodResolver(shiftEntrySchema),
    mode: 'onSubmit',
    defaultValues: {
      shiftName: '',
      startTime: '08:00',
      endTime: '17:00',
      breakStart: '',
      breakEnd: '',
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    }
  });

  useEffect(() => {
    if (value) {
      reset({
        shiftName: value.shiftName || '',
        startTime: value.startTime || '08:00',
        endTime: value.endTime || '17:00',
        breakStart: value.breakStart || '',
        breakEnd: value.breakEnd || '',
        monday: value.monday || false,
        tuesday: value.tuesday || false,
        wednesday: value.wednesday || false,
        thursday: value.thursday || false,
        friday: value.friday || false,
        saturday: value.saturday || false,
        sunday: value.sunday || false
      });
    } else {
      reset({
        shiftName: '',
        startTime: '08:00',
        endTime: '17:00',
        breakStart: '',
        breakEnd: '',
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      });
    }
    setVisible(true);
  }, [value, reset]);

  const onSubmit = async (data: ShiftEntryFormData) => {
    try {
      const shiftEntry: ShiftTemplateEntryDto = {
        uuid: value?.uuid,
        shiftTemplateId: value?.shiftTemplateId,
        shiftName: data.shiftName,
        startTime: data.startTime,
        endTime: data.endTime,
        breakStart: data.breakStart || undefined,
        breakEnd: data.breakEnd || undefined,
        monday: Boolean(data.monday),
        tuesday: Boolean(data.tuesday),
        wednesday: Boolean(data.wednesday),
        thursday: Boolean(data.thursday),
        friday: Boolean(data.friday),
        saturday: Boolean(data.saturday),
        sunday: Boolean(data.sunday),
        position: value?.position || 0
      };
      
      await onSave(shiftEntry);
      setVisible(false);
    } catch (error) {
      handleError(error, 'saving shift entry');
    }
  };

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  const dialogContent = useCallback(() => {
    return (
      <div className="p-4 space-y-4">
        {/* Shift Name */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.shiftName')} *
            </label>
          <Controller
            name="shiftName"
            control={control}
            render={({ field }) => (
              <TextBoxComponent
                {...field}
                placeholder={t('shiftModelTemplate.form.shiftNamePlaceholder')}
                cssClass={errors.shiftName ? 'e-error' : ''}
              />
            )}
          />
          {errors.shiftName && (
            <p className="text-red-500 text-sm mt-1">
              {resolveValidationMessage(t, errors.shiftName)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.startTime')} *
            </label>
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  type="time"
                  cssClass={errors.startTime ? 'e-error' : ''}
                />
              )}
            />
            {errors.startTime && (
              <p className="text-red-500 text-sm mt-1">
                {resolveValidationMessage(t, errors.startTime)}
              </p>
            )}
          </div>

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.endTime')} *
            </label>
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  type="time"
                  cssClass={errors.endTime ? 'e-error' : ''}
                />
              )}
            />
            {errors.endTime && (
              <p className="text-red-500 text-sm mt-1">
                {resolveValidationMessage(t, errors.endTime)}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Break Start */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.breakStart')}
            </label>
            <Controller
              name="breakStart"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  type="time"
                />
              )}
            />
          </div>

          {/* Break End */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.breakEnd')}
            </label>
            <Controller
              name="breakEnd"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  type="time"
                />
              )}
            />
          </div>
        </div>

        {/* Days of Week */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('shiftModelTemplate.form.daysOfWeek')}
          </label>
          <div className="grid grid-cols-7 gap-2">
            <div className="flex items-center">
              <Controller
                name="monday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.monday')}
                  />
                )}
              />
            </div>
            <div className="flex items-center">
              <Controller
                name="tuesday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.tuesday')}
                  />
                )}
              />
            </div>
            <div className="flex items-center">
              <Controller
                name="wednesday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.wednesday')}
                  />
                )}
              />
            </div>
            <div className="flex items-center">
              <Controller
                name="thursday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.thursday')}
                  />
                )}
              />
            </div>
            <div className="flex items-center">
              <Controller
                name="friday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.friday')}
                  />
                )}
              />
            </div>
            <div className="flex items-center">
              <Controller
                name="saturday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.saturday')}
                  />
                )}
              />
            </div>
            <div className="flex items-center">
              <Controller
                name="sunday"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    checked={field.value}
                    change={(e: any) => field.onChange(e.checked)}
                    label={t('shiftModelTemplate.form.sunday')}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [control, errors, t]);

  return (
    <DialogComponent
      width="600px"
      isModal={true}
      visible={visible}
      close={handleClose}
      header={value ? t('shiftModelTemplate.form.editEntryTitle') : t('shiftModelTemplate.form.createEntryTitle')}
      footerTemplate={() => (
        <div className="flex justify-end space-x-2">
          <ButtonComponent
            type="button"
            onClick={handleClose}
            cssClass="e-outline"
          >
            {t('actions.cancel')}
          </ButtonComponent>
          <ButtonComponent
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            cssClass="e-primary"
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </ButtonComponent>
        </div>
      )}
    >
      {dialogContent()}
    </DialogComponent>
  );
};

export default ShiftEntryFormDialog; 